import {
  EXERCISE_REMARK_LIMITS,
  EXERCISE_REMARK_STATUS_LABELS,
  type ExerciseRemarkDto,
} from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';
import { TRAINING_PLANS_RESOURCE } from '../../lib/training';
import { Badge, Button, Dialog, Field, Textarea, useToast } from '../ui';

/**
 * Hinweise zu einer Uebung: wer etwas bemerkt (falsche Angabe, unklare Skizze, Wunsch), schreibt es hier
 * auf. Die Verantwortlichen sehen alle Hinweise in der Liste und erledigen sie, der Verfasser sieht an der
 * Uebung seinen Hinweis samt Status und Antwort.
 */

export const remarksKey = (exerciseId: string): unknown[] => ['training-exercise-remarks', exerciseId];

export function RemarkStatusBadge({ remark }: { remark: Pick<ExerciseRemarkDto, 'status'> }): JSX.Element {
  return <Badge tone={remark.status === 'DONE' ? 'success' : 'warning'}>{EXERCISE_REMARK_STATUS_LABELS[remark.status]}</Badge>;
}

function RemarkDialog({ exerciseId, title, onClose }: { exerciseId: string; title: string; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => api.post<ExerciseRemarkDto>(`/api/training/exercises/${encodeURIComponent(exerciseId)}/remarks`, { text: text.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: remarksKey(exerciseId) });
      await queryClient.invalidateQueries({ queryKey: ['training-remarks'] });
      await queryClient.invalidateQueries({ queryKey: ['training-remarks-open-count'] });
      toast.show('Danke – dein Hinweis ist angekommen');
      onClose();
    },
    onError: (failure: unknown) => setError(failure instanceof ApiError ? failure.message : 'Der Hinweis konnte nicht gesendet werden.'),
  });

  const submit = (): void => {
    if (!text.trim()) { setError('Bitte schreibe, was dir aufgefallen ist.'); return; }
    setError(null);
    send.mutate();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Hinweis zur Übung"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={send.isPending}>Abbrechen</Button>
          <Button onClick={submit} loading={send.isPending}>Hinweis senden</Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">Übung: <span className="font-medium text-slate-900">{title}</span></p>
        <Field label="Was ist dir aufgefallen?" error={error ?? undefined} hint={`${text.length} von ${EXERCISE_REMARK_LIMITS.text} Zeichen`}>
          {(id) => (
            <Textarea
              id={id}
              rows={5}
              value={text}
              maxLength={EXERCISE_REMARK_LIMITS.text}
              autoFocus
              onChange={(event) => setText(event.target.value)}
              placeholder="z. B. Die Skizze passt nicht zum Ablauf, oder: Altersangabe stimmt nicht."
            />
          )}
        </Field>
        <p className="text-sm text-slate-500">Dein Hinweis geht an die Verantwortlichen der Trainingsplanung. Du siehst hier, ob er erledigt ist.</p>
      </div>
    </Dialog>
  );
}

/** Knopf "Hinweis zur Übung" samt der Hinweise, die die Person hier sehen darf. */
export function ExerciseRemarks({ exerciseId, title }: { exerciseId: string; title: string }): JSX.Element {
  const { can } = useAuth();
  const manages = can(TRAINING_PLANS_RESOURCE, 'manage');
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: remarksKey(exerciseId),
    queryFn: () => api.get<ExerciseRemarkDto[]>(`/api/training/exercises/${encodeURIComponent(exerciseId)}/remarks`),
    // Ein anderer erledigt den Hinweis, waehrend dieser Reiter offen bleibt: beim Oeffnen immer frisch laden.
    refetchOnMount: 'always',
  });
  const remarks = data ?? [];

  return (
    <section aria-label="Hinweise zur Übung" className="space-y-2 print:hidden">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>Hinweis zur Übung</Button>
        {remarks.length > 0 && (
          <span className="text-sm text-slate-600">{manages ? 'Hinweise zu dieser Übung' : 'Meine Hinweise'}</span>
        )}
      </div>
      {remarks.length > 0 && (
        <ul className="space-y-2">
          {remarks.map((remark) => (
            <li key={remark.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <RemarkStatusBadge remark={remark} />
                <span className="text-slate-500">{remark.mine ? 'von dir' : remark.authorName} · {formatDateTime(remark.createdAt)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap break-words text-slate-800">{remark.text}</p>
              {remark.answer && (
                <p className="mt-2 whitespace-pre-wrap break-words rounded-lg bg-white p-2 text-slate-700">
                  <span className="font-medium">Antwort{remark.resolvedByName ? ` von ${remark.resolvedByName}` : ''}:</span> {remark.answer}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && <RemarkDialog exerciseId={exerciseId} title={title} onClose={() => setOpen(false)} />}
    </section>
  );
}
